import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaClock, FaBolt, FaSmile, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const metrics = [
  { label: 'Happy Customers', value: 512, suffix: '+', icon: <FaSmile className="text-2xl text-yellow-500" /> },
  { label: 'Days Saved per Month', value: 12, icon: <FaClock className="text-2xl text-orange-500" /> },
  { label: 'Efficiency Improvement', value: 3, suffix: 'x', icon: <FaBolt className="text-2xl text-green-600" /> },
];

// Changes every week automatically
const getWeekIndex = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now - start) / (24 * 60 * 60 * 1000));
  return Math.floor(days / 7);
};

// Pick N items for the current week
const pickWeekly = (list, count = 6) => {
  if (!Array.isArray(list) || list.length === 0) return [];
  const week = getWeekIndex();
  const start = week % list.length;
  const picked = [];
  for (let i = 0; i < Math.min(count, list.length); i++) {
    picked.push(list[(start + i) % list.length]);
  }
  return picked;
};

// Optional fallback avatar (if image fails)
const InitialAvatar = ({ name }) => {
  const initials = (name || 'User')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join('');
  return (
    <div className="w-16 h-16 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold shadow">
      {initials}
    </div>
  );
};

const TrustTestimonials = () => {
  const [animatedValues, setAnimatedValues] = useState(metrics.map(() => 0));
  const [allTestimonials, setAllTestimonials] = useState([]);
  const [brokenAvatars, setBrokenAvatars] = useState({});
  const sliderRef = useRef(null);

  // Load testimonials from JSON
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${process.env.PUBLIC_URL}/testimonials.json`);
        const json = await res.json();
        setAllTestimonials(json.testimonials || []);
      } catch (e) {
        console.error('Failed to load testimonials.json', e);
        setAllTestimonials([]);
      }
    };
    load();
  }, []);

  // Rotate weekly (6 testimonials per week)
  const testimonials = useMemo(() => pickWeekly(allTestimonials, 6), [allTestimonials]);

  // Counter animation
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedValues(prev =>
        prev.map((val, i) => (val < metrics[i].value ? val + 1 : val))
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const scroll = (direction) => {
    const container = sliderRef.current;
    const scrollAmount = 340;
    if (container) {
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Auto-slide (only if we have enough cards)
  useEffect(() => {
    if (!testimonials || testimonials.length < 2) return;
    const autoScroll = setInterval(() => scroll('right'), 4200);
    return () => clearInterval(autoScroll);
  }, [testimonials]);

  return (
    <div className="bg-white py-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Trust Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center mb-16">
          {metrics.map((metric, index) => (
            <div key={index} className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition">
              <div className="flex justify-center mb-4">{metric.icon}</div>
              <div className="text-4xl font-bold text-gray-800">
                {animatedValues[index]}
                {metric.suffix || ''}
              </div>
              <div className="text-gray-600 mt-2 text-lg">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-2">What Our Users Say</h2>

        <p className="text-center text-gray-500 mb-2 text-sm">
          Fresh feedback rotates weekly from professionals across Maharashtra.
        </p>

        <div className="relative">
          {/* Left */}
          <button
            onClick={() => scroll('left')}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow z-10"
            aria-label="Scroll testimonials left"
          >
            <FaChevronLeft className="text-gray-600" />
          </button>

          {/* Slider */}
          <div
            ref={sliderRef}
            className="flex gap-6 overflow-x-auto scroll-smooth no-scrollbar px-2 py-2"
          >
            {testimonials.length === 0 ? (
              <div className="text-gray-500 p-6">Loading testimonials...</div>
            ) : (
              testimonials.map((t, idx) => (
                <div
                  key={`${t.name}-${idx}`}
                  className="w-[320px] flex-shrink-0 rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition p-6"
                >
                  <div className="flex items-center gap-4 mb-4">
                    {!brokenAvatars[idx] && t.avatar ? (
                      <img
                        src={t.avatar}
                        alt={`${t.name} photo`}
                        className="w-14 h-14 rounded-full object-cover border border-gray-200"
                        loading="lazy"
                        onError={() => setBrokenAvatars(prev => ({ ...prev, [idx]: true }))}
                      />
                    ) : (
                      <InitialAvatar name={t.name} />
                    )}

                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{t.name}</div>
                      <div className="text-xs text-gray-600 truncate">
                        {t.role}{t.location ? ` • ${t.location}` : ''}
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 text-sm leading-relaxed">
                    “{t.quote}”
                  </p>
                </div>
              ))
            )}
          </div>

          {/* Right */}
          <button
            onClick={() => scroll('right')}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow z-10"
            aria-label="Scroll testimonials right"
          >
            <FaChevronRight className="text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrustTestimonials;
